import {
  Box,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Crown,
  DollarSign,
  Home,
  LogOut,
  User,
  Zap,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import httpService from "../../services/httpService";

interface NavItem {
  id: string;
  label: string;
  icon: React.FC<{ className?: string }>;
  path: string;
  badge?: number;
}

const NAV_ITEMS: NavItem[] = [
  { id: "dashboard", label: "Dashboard", icon: Home, path: "/provider/dashboard" },
  { id: "portfolios", label: "Portfolios", icon: Briefcase, path: "/provider/portfolios" },
  { id: "baskets", label: "Baskets", icon: Box, path: "/provider/baskets" },
  { id: "options", label: "Options Screener", icon: Zap, path: "/provider/options" },

];

const ProviderSidebarV3: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [providerLogoUrl, setProviderLogoUrl] = useState<string | null>(null);
  const [loadingLogo, setLoadingLogo] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  const userRoles = user?.userRoles?.map((ur) => ur.role) || [];
  const isAdmin = userRoles.includes("ADMIN");
  const isInvestor = userRoles.includes("INVESTOR") || userRoles.length === 0;

  // PWA-2369: Earnings shows for any provider who can actually use it -- the API
  // is provider self-service: requireAnyRole(['PROVIDER']) then
  // resolveUserProvider(), so the caller needs the PROVIDER role AND a provider
  // mapping (or it 403s). Matches the route gate (["PROVIDER"]).
  const providerId = user?.provider?.id || user?.userProviders?.[0]?.providerId;
  const providerName = user?.provider?.name || user?.userProviders?.[0]?.providers?.name || "Provider";

  const isProvider = userRoles.includes("PROVIDER");
  const hasProvider = Boolean(providerId);
  const navItems: NavItem[] = [
    ...NAV_ITEMS,
    ...(isProvider && hasProvider
      ? [{ id: "earnings", label: "Earnings", icon: DollarSign, path: "/provider/earnings" }]
      : []),
    ...(providerId
      ? [{ id: "profile", label: "Profile", icon: User, path: "/provider/profile" }]
      : []),
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  useEffect(() => {
    const fetchProviderLogo = async () => {
      if (!providerId) {
        setProviderLogoUrl(null);
        return;
      }
      try {
        setLoadingLogo(true);
        const response = await httpService.get(`/providers/${providerId}/logo`);
        if (response?.data?.logoUrl) {
          setProviderLogoUrl(response.data.logoUrl);
        } else {
          setProviderLogoUrl(null);
        }
      } catch {
        setProviderLogoUrl(null);
      } finally {
        setLoadingLogo(false);
      }
    };
    fetchProviderLogo();
  }, [providerId]);

  const isActive = (item: NavItem) => {
    if (item.path === "/provider/dashboard") {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <aside
      className={`bg-rp-black border-r border-rp-separator flex flex-col shrink-0 transition-all duration-300 ${
        isCollapsed ? "w-16" : "w-64"
      } hidden md:flex`}
    >
      {/* Header */}
      <div
        className={`flex items-center ${
          isCollapsed ? "flex-col justify-center px-2 gap-3" : "justify-between px-6"
        } pt-6 pb-4`}
      >
        {!isCollapsed && (
          <div className="flex items-center">
            <img src="/favicon.png" alt="Plutus" className="h-8 w-8 mr-2" />
            <img src="/dark.png" alt="Plutus" className="h-8 w-auto" />
            <span className="ml-2 text-sm font-medium text-rp-amber">for Providers</span>
          </div>
        )}
        {isCollapsed && (
          <img src="/favicon.png" alt="Plutus" className="h-8 w-8" />
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1.5 rounded-lg hover:bg-rp-separator text-rp-grey transition-colors"
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Provider Logo */}
      {!isCollapsed && (
        <div className="px-6 pt-7 pb-6">
          <div className="flex flex-col items-center">
            <div className="flex-shrink-0">
              {loadingLogo ? (
                <div className="w-24 h-24 rounded-lg flex items-center justify-center bg-rp-separator">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rp-amber"></div>
                </div>
              ) : providerLogoUrl ? (
                <img
                  src={providerLogoUrl}
                  alt="Provider logo"
                  className="w-24 h-24 object-cover rounded-lg"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-24 h-24 rounded-lg flex items-center justify-center bg-rp-separator">
                  <Briefcase className="w-12 h-12 text-rp-amber" />
                </div>
              )}
            </div>
            <p className="text-sm font-medium text-rp-grey mt-2 text-center">{providerName}</p>
          </div>
        </div>
      )}
      {isCollapsed && (
        <div className="px-2 pt-4 pb-4">
          {loadingLogo ? (
            <div className="w-12 h-12 mx-auto rounded-lg flex items-center justify-center bg-rp-separator">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-rp-amber"></div>
            </div>
          ) : providerLogoUrl ? (
            <img
              src={providerLogoUrl}
              alt="Provider logo"
              className="w-12 h-12 mx-auto object-cover rounded-lg"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
              }}
            />
          ) : (
            <div className="w-12 h-12 mx-auto rounded-lg flex items-center justify-center bg-rp-separator">
              <Briefcase className="w-6 h-6 text-rp-amber" />
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-1 mt-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 rounded-lg transition-all text-sm font-medium ${
                isCollapsed ? "justify-center px-2 py-2.5" : "px-4 py-2.5"
              } ${
                active
                  ? "bg-rp-amber/10 text-rp-amber border-l-[3px] border-rp-amber"
                  : "text-rp-grey hover:text-rp-grey hover:bg-rp-separator border-l-[3px] border-transparent"
              }`}
            >
              <Icon className="w-5 h-5 shrink-0" />
              {!isCollapsed && (
                <>
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="bg-rp-amber text-rp-black text-[10px] px-1.5 py-0.5 rounded font-bold">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}
      </nav>

      {/* Bottom Navigation */}
      <div className="mt-auto" />
      <div className="border-t border-rp-separator p-2 space-y-1">
        {isAdmin && (
          <button
            onClick={() => navigate("/admin/dashboard")}
            className={`w-full flex items-center gap-3 text-xs font-medium rounded-lg transition-colors border border-red-700 text-white hover:bg-red-900/20 ${
              isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
            }`}
          >
            <Crown className="w-5 h-5 shrink-0" />
            {!isCollapsed && <span>Admin Dashboard</span>}
          </button>
        )}

        {isInvestor && (
          <button
            onClick={() => navigate("/alpha")}
            className={`w-full flex items-center gap-3 text-xs font-medium rounded-lg transition-colors border border-rp-amber text-white hover:bg-rp-amber/20 ${
              isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
            }`}
          >
            <Home className="w-5 h-5 shrink-0" />
            {!isCollapsed && <span>Main Dashboard</span>}
          </button>
        )}


        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 text-sm font-medium rounded-lg transition-colors text-red-500 hover:bg-red-500/20 hover:text-red-500 ${
            isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
          }`}
        >
          <LogOut className="w-5 h-5 shrink-0" />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default ProviderSidebarV3;
